/**
 * Converts a campaign title to a URL-friendly slug
 * @param {string} title - The campaign title
 * @returns {string} - URL-friendly slug
 */
export const createSlugFromTitle = (title) => {
  if (!title) return '';
  
  return title
    .toString()
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[^\w\s-]/g, '') // Remove special characters except word chars, spaces, and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Normalizes a slug for comparison (handles URL decoding)
 * @param {string} slug - The slug from URL
 * @returns {string} - Normalized slug
 */
export const normalizeSlug = (slug) => {
  if (!slug) return '';
  
  try {
    // Decode URL-encoded characters
    const decoded = decodeURIComponent(slug);
    return decoded.toLowerCase().trim();
  } catch (e) {
    // If decoding fails, return as is
    return slug.toLowerCase().trim();
  }
};

/**
 * Compares a title with a slug to check if they match
 * @param {string} title - The campaign title
 * @param {string} slug - The slug from URL
 * @returns {boolean} - True if they match
 */
export const titleMatchesSlug = (title, slug) => {
  if (!title || !slug) return false;
  
  const titleSlug = createSlugFromTitle(title);
  const normalizedSlug = normalizeSlug(slug);
  
  // Also try matching with the slug created from normalized slug (in case of double encoding)
  const normalizedSlugAsSlug = createSlugFromTitle(normalizedSlug);
  
  return titleSlug === normalizedSlug || titleSlug === normalizedSlugAsSlug;
};

/**
 * Generates the campaign URL path based on redirects data
 * @param {Object} campaign - The campaign object with redirects array
 * @param {string} fallbackTitle - Optional fallback title if redirects not available (for backward compatibility)
 * @returns {string|null} - The URL path (e.g., "/campaign/primary-url" or "/story/primary-url") or null if no redirects
 */
export const getCampaignUrl = (campaign, fallbackTitle = null) => {
  // Check if campaign has redirects array with data
  if (campaign && campaign.redirects && Array.isArray(campaign.redirects) && campaign.redirects.length > 0) {
    const redirect = campaign.redirects[0];
    const primaryUrl = redirect.primary_url;
    const isStoryCampaign = redirect.is_story_campaign === true;
    
    if (primaryUrl) {
      if (isStoryCampaign) {
        return `/story/${primaryUrl}`;
      }
      return `/campaign/${primaryUrl}`;
    }
  }
  
  // If no redirects, return null (will trigger 404)
  return null;
};

/**
 * Legacy function for backward compatibility - generates URL from title
 * @deprecated Use getCampaignUrl with campaign object instead
 * @param {string} title - The campaign title
 * @param {boolean} isStoryCampaign - Whether this is a story/medical campaign
 * @returns {string} - The URL path
 */
export const getCampaignUrlFromTitle = (title, isStoryCampaign = false) => {
  if (!title) return '/campaigns';
  
  const slug = createSlugFromTitle(title);
  if (isStoryCampaign) {
    return `/story/${slug}`;
  }
  return `/campaign/${slug}`;
};

/**
 * For life campaigns with multiple redirect rows,
 * use primary_url only from rows where is_life_campaign === true.
 * @param {Object} campaign - campaign object containing redirects
 * @param {string} fallback - fallback slug if no life redirect exists
 * @returns {string}
 */
export const getLifePrimarySlugFromRedirects = (campaign, fallback = '') => {
  const redirects = Array.isArray(campaign?.redirects) ? campaign.redirects : [];
  const lifeRedirect = redirects.find(
    (r) => Number(r?.is_life_campaign) === 1 || r?.is_life_campaign === true
  );
  const primary = lifeRedirect?.primary_url;

  if (primary != null && String(primary).trim() !== '') {
    return String(primary).trim();
  }

  return fallback != null ? String(fallback).trim() : '';
};

